import { Observable, catchError, finalize, map, of, shareReplay, tap } from 'rxjs';

import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { PlatformAuthApiService } from '@core/api/platform-auth-api.service';
import { PlatformAuthSuccessDataDto } from '@core/models/platform-auth.dto';
import { PlatformSessionService } from '@core/platform-auth/platform-session.service';

@Injectable({ providedIn: 'root' })
export class PlatformAuthOrchestratorService {
  private readonly authApi = inject(PlatformAuthApiService);
  private readonly router = inject(Router);
  private readonly session = inject(PlatformSessionService);

  private refreshInFlight$: Observable<boolean> | null = null;

  completeLogin(data: PlatformAuthSuccessDataDto, rememberMe: boolean): string {
    this.session.setAuthenticatedSession(data, rememberMe);
    return this.resolvePostLoginUrl();
  }

  logoutAndNavigatePublic(): Observable<void> {
    const refreshToken = this.session.refreshToken();
    this.session.clearSession();
    void this.router.navigateByUrl('/');

    return this.authApi.logout(refreshToken).pipe(
      map(() => void 0),
      catchError(() => of(void 0)),
    );
  }

  redirectToLoginWithReturnUrl(path: string): void {
    this.session.saveReturnUrl(path);
    void this.router.navigate(['/entrar']);
  }

  refreshSession(): Observable<boolean> {
    if (!this.session.hasPersistedSession()) {
      this.session.clearSession();
      return of(false);
    }

    if (this.refreshInFlight$ != null)
      return this.refreshInFlight$;

    const refreshToken = this.session.refreshToken();
    if (refreshToken == null || refreshToken === '')
      return of(false);

    this.refreshInFlight$ = this.authApi.refresh(refreshToken).pipe(
      tap((data: PlatformAuthSuccessDataDto) => this.session.setAuthenticatedSession(data, this.session.rememberMe())),
      map(() => true),
      catchError((_err: unknown) => {
        this.session.clearSession();

        return of(false);
      }),
      finalize(() => this.refreshInFlight$ = null),
      shareReplay(1),
    );

    return this.refreshInFlight$;
  }

  resolvePostLoginUrl(): string {
    const returnUrl = this.session.consumeReturnUrl();
    if (this.session.mustChangePassword)
      return '/admin/conta/trocar-senha';

    if (returnUrl != null)
      return returnUrl;

    // Tela inicial pós-login: Admin e Editor (mesma rota, guardas por rota)
    return '/admin/imoveis';
  }
}
